/**
 * Cutscene sequencing, as a pure function of elapsed time — no timers, no
 * DOM, so a whole run can be stepped and asserted in a test.
 *
 * Timing follows the interpreter's model exactly: a tick's commands run at
 * the instant it is reached, then the sequence holds for that tick's delay
 * before the next one. `SET DELAY` persists, which the tick list has already
 * resolved, so the machine only has to accumulate.
 *
 * Moved out of `shell/intro/intro-machine.ts`, which now re-exports these
 * under their original names (`IntroState`/`createIntroState`/`advanceIntro`/
 * `skipIntro`) so the intro's own tests are untouched — only the song-cue
 * table (`INTRO_SONG_CUES`/`songCueAt`), which is genuinely TITLE.TTM-
 * specific inferred timing, stayed behind.
 */

import type { CutsceneTick } from "./cutscene-ticks.ts";

export interface CutsceneState {
  readonly elapsedMs: number;
  /** Index of the next tick that has not run yet; equal to `ticks.length` when the sequence is spent. */
  readonly nextTick: number;
  /** Wall-clock offset at which `nextTick` fires. */
  readonly nextTickAtMs: number;
  readonly finished: boolean;
  /** `true` when the player cut it short rather than letting it run out. */
  readonly skipped: boolean;
}

export function createCutsceneState(): CutsceneState {
  return { elapsedMs: 0, nextTick: 0, nextTickAtMs: 0, finished: false, skipped: false };
}

export interface CutsceneStep {
  readonly state: CutsceneState;
  /** Ticks whose commands became due during this step, in order. Usually empty or one. */
  readonly fired: readonly CutsceneTick[];
}

export function advanceCutscene(ticks: readonly CutsceneTick[], state: CutsceneState, elapsedMs: number): CutsceneStep {
  if (state.finished) return { state: { ...state, elapsedMs }, fired: [] };

  const fired: CutsceneTick[] = [];
  let index = state.nextTick;
  let dueAt = state.nextTickAtMs;
  while (index < ticks.length && dueAt <= elapsedMs) {
    const tick = ticks[index];
    if (!tick) break;
    fired.push(tick);
    dueAt += tick.delayMs;
    index += 1;
  }
  return {
    state: { elapsedMs, nextTick: index, nextTickAtMs: dueAt, finished: index >= ticks.length, skipped: false },
    fired,
  };
}

/**
 * A key or click during the original's cutscenes jumps straight past the
 * rest of the script; there is no per-slide skip (confirmed live for the
 * intro, `docs/game/ui-reference/intro.md`, and applied uniformly to every
 * other cutscene the same interpreter now drives).
 */
export function skipCutscene(ticks: readonly CutsceneTick[], state: CutsceneState): CutsceneState {
  return { ...state, nextTick: ticks.length, finished: true, skipped: true };
}
